import type { RebateTierTranslatedContent } from "../useRebateTierTranslatedContent";

import { ShieldProtectedIcon } from "../../Icons";
import { PopoverMenu } from "../../PopoverMenu";
import { Typo } from "../../Typo";
import { ProtectionCard } from "../modals/ProtectionCard";

type ProtectionBadgeProps = {
  daysRemaining: number | null;
  freezePeriodValidTo: string | null;
  content: RebateTierTranslatedContent;
  isMobile: boolean;
  isOpen: boolean;
  onOpen: () => void;
};

const TRIGGER_CLASS =
  "flex h-8 shrink-0 items-center justify-center rounded-full bg-surface-accent-blue-subtler p-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stroke-focused";

export const ProtectionBadge = ({
  daysRemaining,
  freezePeriodValidTo,
  content,
  isMobile,
  isOpen,
  onOpen,
}: ProtectionBadgeProps) => {
  const daysLabel = content.protectionDays(daysRemaining);
  const accessibleName = `${content.protectionEnds} ${daysLabel}`;
  const label = (
    <span className="flex items-center gap-1 px-2">
      <ShieldProtectedIcon
        aria-hidden="true"
        className="h-4 w-3 shrink-0 text-icon-brand-boldest"
      />
      <Typo element="span" type="labelSM" textColor="text-text-brand-boldest">
        {daysLabel}
      </Typo>
    </span>
  );

  if (isMobile) {
    return (
      <button
        type="button"
        aria-label={accessibleName}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={onOpen}
        className={TRIGGER_CLASS}
        data-testid="rebate_tier_protection_badge_button"
      >
        {label}
      </button>
    );
  }

  return (
    <PopoverMenu
      place="bottom"
      align="start"
      contentClassName="w-96 max-w-[calc(100vw-32px)] p-6"
      content={
        <ProtectionCard
          freezePeriodValidTo={freezePeriodValidTo}
          content={content}
        />
      }
      asChild
    >
      <button
        type="button"
        aria-label={accessibleName}
        className={TRIGGER_CLASS}
        data-testid="rebate_tier_protection_badge_button"
      >
        {label}
      </button>
    </PopoverMenu>
  );
};
